/**
 * 메인 "인기 시설 TOP 5" 위젯 — 포털 스타일 리스트.
 *
 * 북마크(찜) 많은 순 `/facilities/popular` API 사용 — 실시간 HOT(평점)과 다른 지표로 구분.
 * 카드형이 아닌 리스트형으로 표시(포털 위젯 패턴).
 */
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Star, MapPin } from 'lucide-react'
import apiClient from '@/lib/api/client'
import Skeleton from '@/components/ui/Skeleton'
import Badge from '@/components/ui/Badge'
import { FACILITY_TYPE_LABELS } from '@/types/common'
import type { FacilitySummary } from '@/types/facility'
import {
  HOME_POPULAR_FACILITIES_QUERY_KEY,
  HOME_POPULAR_BY_BOOKMARK_PATH,
} from '@/features/home/widgetQueries'

export default function PopularFacilities() {
  // 쿼리 키·경로는 SSR 프리페치(entry-server.tsx)와 공유 — widgetQueries.ts
  const { data, isLoading } = useQuery({
    queryKey: HOME_POPULAR_FACILITIES_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get(HOME_POPULAR_BY_BOOKMARK_PATH)
      return res.data.data as FacilitySummary[]
    },
    staleTime: 5 * 60 * 1000,
  })

  return (
    <ModuleCard title="인기 시설 TOP 5" moreHref="/facilities">
      {isLoading ? (
        <ul className="flex flex-col">
          {Array.from({ length: 5 }).map((_, i) => (
            <li key={i} className="border-b border-cb-gray-100 py-3 last:border-0">
              <Skeleton height="0.875rem" width="80%" />
              <Skeleton height="0.75rem" width="50%" className="mt-1.5" />
            </li>
          ))}
        </ul>
      ) : (
        <ul className="flex flex-col">
          {(data ?? []).slice(0, 5).map((f, idx) => (
            <li key={f.id} className="border-b border-cb-gray-100 last:border-0">
              <Link
                to={`/facilities/${f.id}`}
                className="flex items-center gap-3 py-2.5 hover:bg-cb-gray-50"
              >
                <span className="w-5 flex-shrink-0 text-center text-sm font-bold text-cb-primary">
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-cb-gray-900">
                    {f.name}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-cb-gray-500">
                    <Badge variant="type" className="text-[10px]">
                      {FACILITY_TYPE_LABELS[f.facilityType] ?? f.facilityType}
                    </Badge>
                    <MapPin size={10} className="text-cb-gray-400" />
                    <span className="truncate">{f.addressSido}</span>
                  </p>
                </div>
                {f.reviewAvgRating && Number(f.reviewAvgRating) > 0 ? (
                  <span className="flex flex-shrink-0 items-center gap-0.5 text-xs font-bold text-amber-600">
                    <Star size={11} className="fill-amber-400 text-amber-400" />
                    {Number(f.reviewAvgRating).toFixed(1)}
                  </span>
                ) : f.evaluationGrade ? (
                  <Badge
                    variant="grade"
                    grade={f.evaluationGrade as 'A' | 'B' | 'C' | 'D' | 'E'}
                    className="text-[10px]"
                  />
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </ModuleCard>
  )
}

function ModuleCard({
  title,
  moreHref,
  children,
}: {
  title: string
  moreHref: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-cb-gray-100 bg-white p-5">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-bold text-cb-gray-900">{title}</h2>
        <Link
          to={moreHref}
          className="flex items-center gap-0.5 text-xs font-medium text-cb-gray-500 hover:text-cb-primary"
        >
          더보기 <ChevronRight size={12} />
        </Link>
      </header>
      {children}
    </section>
  )
}
